import { toast } from '@/clientWeb/components/ui/toast-utils'
import { deviceInUse, deviceManager, type MediaKind } from './device-manager'
import { useDevicesStore } from '../store/devices.store'
import { readPermissionStates } from '../utils/device-permissions'

/**
 * The gate every "turn my camera/microphone ON" goes through.
 *
 * Turning a device OFF is a decision the user is always allowed to make.
 * Turning one ON is a request the browser can refuse, and the app used to treat
 * the two as the same boolean flip: `micEnabled`/`camEnabled` defaulted to
 * `true`, nothing lowered them when `getUserMedia` was refused, and the toggles
 * flipped a flag without ever asking. So a user who dismissed the permission
 * prompt saw a lit microphone button in the lobby, carried it into the meeting
 * through VcApp, and clicking it did nothing but change the icon.
 *
 * This module answers one question — *may we open this device?* — and records
 * the answer in the devices store, which is what lowers the toggle. It holds no
 * opinion about what the caller does next, so the lobby can open its
 * step-by-step unblock dialog and the meeting can raise a toast.
 *
 * Deliberately a separate module from the store: it needs `deviceManager`, and
 * `deviceManager` already imports the store for its capture presets. Putting
 * the gate in the store would close that loop.
 */

export type PermissionOutcome = 'granted' | 'denied' | 'no-device' | 'in-use' | 'error'

function classify(error: unknown): PermissionOutcome {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError') return 'denied'
    if (error.name === 'NotFoundError') return 'no-device'
  }
  if (deviceInUse(error)) return 'in-use'
  return 'error'
}

/**
 * Make sure we may open `kind`, prompting for it if the browser has not decided
 * yet, and record the verdict.
 *
 * Awaiting this before flipping a toggle is the whole point: on `'granted'` the
 * device is already open (or was already running), so the toggle turns on
 * something real.
 */
export async function ensureDevicePermission(kind: MediaKind): Promise<PermissionOutcome> {
  const { setDevicePermission } = useDevicesStore.getState()

  if (!deviceManager.isSupported()) {
    setDevicePermission(kind, 'error')
    return 'error'
  }

  // Already running. The browser does not hand out a live track without a
  // grant, and asking again would be a second prompt for a device we hold.
  if (deviceManager.getTrack(kind)) {
    setDevicePermission(kind, 'granted')
    return 'granted'
  }

  const states = await readPermissionStates()
  const known = kind === 'audio' ? states.microphone : states.camera
  if (known === 'denied') {
    // A block is remembered per origin and the browser will NOT prompt again:
    // the capture below would reject without showing the user anything, which
    // reads as a broken button. Report it instead, so the caller can say how to
    // undo it.
    setDevicePermission(kind, 'denied')
    return 'denied'
  }

  // `'prompt'`, or a browser without the descriptors — Firefox has neither and
  // Safari is partial, so both come back `'prompt'` there. Either way the only
  // way to find out is to ask, which is what this capture does.
  //
  // `'granted'` opens the device too, though nothing needs asking. A grant
  // says we MAY open it, not that it will open: answering from the
  // permission alone lit the meeting's camera button over a black tile for
  // the second it took a camera held by another browser to refuse, then
  // turned it back off. Opened here, the toggle moves only once there is a
  // picture behind it, and the claim that follows takes this same track.
  try {
    await deviceManager.prime({ audio: kind === 'audio', video: kind === 'video' })
    setDevicePermission(kind, 'granted')
    return 'granted'
  } catch (err) {
    const outcome = classify(err)
    setDevicePermission(kind, outcome === 'no-device' || outcome === 'in-use' ? 'error' : outcome)
    return outcome
  }
}

/**
 * Say out loud why a toggle did not turn on.
 *
 * A control that refuses to move and says nothing is indistinguishable from one
 * that is broken, and the fix — unblocking the site in browser settings — is
 * not something the user will guess. Same reasoning, and the same channel, as
 * `useDeviceIssues`.
 */
export function notifyDeviceBlocked(kind: MediaKind, outcome: PermissionOutcome): void {
  if (outcome === 'granted') return
  // Already said, by the device manager through `useDeviceIssues`, which is
  // where every other route to a busy device is reported too.
  if (outcome === 'in-use') return
  const Device = kind === 'audio' ? 'Microphone' : 'Camera'
  const device = kind === 'audio' ? 'microphone' : 'camera'

  if (outcome === 'no-device') {
    toast.warning({
      title: `No ${device} found`,
      description: `Connect a ${device} and try again.`,
    })
    return
  }
  if (outcome === 'denied') {
    toast.warning({
      title: `${Device} is blocked`,
      description: `Allow ${device} access from the icon in your browser's address bar, then try again.`,
    })
    return
  }
  toast.error({
    title: `Couldn't turn on your ${device}`,
    description: `The ${device} could not be opened. Check that no other app is using it.`,
  })
}

/**
 * Re-open a device after the browser's grant has (or might have) come back —
 * the "Allow" button in the meeting's permission-recovery dialog, or the
 * Permissions `change` listener firing `granted`.
 *
 * `ensureDevicePermission` prompts when the state is `'prompt'` and primes the
 * device on a grant; `deviceManager.reacquire` then moves the running producer
 * onto the fresh track (see its note) with no page reload. Returns whether the
 * device is live again. False when the browser still refuses it: a hard
 * `'denied'` block rejects without a prompt, so the caller keeps the
 * manual-unblock instructions up and lets the `change` listener recover when
 * the user flips the setting back.
 */
export async function reacquireAfterGrant(kind: MediaKind): Promise<boolean> {
  // Snapshot the session generation BEFORE the permission prompt: if the user
  // leaves the meeting while the prompt (or prime) is up, `reacquire` must not
  // reopen the device for a session that is gone.
  const generation = deviceManager.currentGeneration(kind)
  const outcome = await ensureDevicePermission(kind)
  if (outcome !== 'granted') return false
  const track = await deviceManager.reacquire(kind, generation)
  return !!track
}
